/**
 * UI Components - Reusable components
 */

import {
  t,
  getCurrentLanguage,
  translateRoomType,
  translateRatePlan,
  translateRatePlanDescription,
  translateAmenity,
  translateRoomDescription,
  getCustomAmenityIcon,
} from './i18n';
import { getActiveServices } from './api';
import { getCachedTenantConfig } from './config';
import { getConfigServices } from './miniapp-config';

/**
 * Language switcher trigger button in header
 */
export function LanguageSwitcher(): string {
  const currentLang = getCurrentLanguage();
  const langConfig: Record<string, { flag: string; label: string }> = {
    th: { flag: '🇹🇭', label: 'TH' },
    en: { flag: '🇬🇧', label: 'EN' },
    zh: { flag: '🇨🇳', label: 'ZH' },
    ru: { flag: '🇷🇺', label: 'RU' },
    ja: { flag: '🇯🇵', label: 'JA' },
    ko: { flag: '🇰🇷', label: 'KO' },
  };
  const current = langConfig[currentLang] || { flag: '🌐', label: currentLang.toUpperCase() };

  return `
    <button class="lang-trigger-btn" type="button" onclick="window.openLangModal?.()" aria-label="Select Language">
      <span class="lang-trigger-flag">${current.flag}</span>
      <span class="lang-trigger-label">${current.label}</span>
      <svg class="lang-trigger-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M6 9l6 6 6-6"/>
      </svg>
    </button>
  `;
}

export interface LanguageItem {
  code: string;
  flag: string;
  name: string;
}

export const SUPPORTED_LANGUAGES: LanguageItem[] = [
  { code: 'th', flag: '🇹🇭', name: 'ภาษาไทย' },
  { code: 'en', flag: '🇬🇧', name: 'English' },
  { code: 'zh', flag: '🇨🇳', name: '中文' },
  { code: 'ru', flag: '🇷🇺', name: 'Русский' },
  { code: 'ja', flag: '🇯🇵', name: '日本語' },
  { code: 'ko', flag: '🇰🇷', name: '한국어' },
];

/**
 * Get active supported languages based on tenant configuration
 */
export function getSupportedLanguages(): LanguageItem[] {
  const cfg = getCachedTenantConfig();
  if (cfg?.supportedLanguages && Array.isArray(cfg.supportedLanguages) && cfg.supportedLanguages.length > 0) {
    const list = SUPPORTED_LANGUAGES.filter(item => cfg.supportedLanguages.includes(item.code));
    if (list.length > 0) return list;
  }
  return SUPPORTED_LANGUAGES;
}

/**
 * Determine if language switcher should be visible (only if > 1 language is supported)
 */
export function shouldShowLanguageSwitcher(): boolean {
  return getSupportedLanguages().length > 1;
}

/**
 * Centered Multi-Language Modal component with Radio Buttons and Backdrop
 */
export function LanguageModal(): string {
  const activeLanguages = getSupportedLanguages();
  if (activeLanguages.length <= 1) {
    return '';
  }
  const currentLang = getCurrentLanguage();

  return `
    <div class="lang-modal-overlay" id="lang-modal-overlay" onclick="if(event.target === this) window.closeLangModal?.()">
      <div class="lang-modal-sheet" role="dialog" aria-modal="true">
        <div class="lang-modal-header">
          <h3 class="lang-modal-title">🌐 ${t('selectLanguage') || 'Select Language'}</h3>
          <button class="lang-modal-close" type="button" onclick="window.closeLangModal?.()" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="lang-modal-list">
          ${activeLanguages.map(lang => {
            const isSelected = currentLang === lang.code;
            return `
              <div 
                class="lang-modal-item ${isSelected ? 'selected' : ''}"
                onclick="window.switchLanguage?.('${lang.code}')"
              >
                <div class="lang-modal-item-left">
                  <span class="lang-modal-flag">${lang.flag}</span>
                  <span class="lang-modal-name">${lang.name}</span>
                </div>
                <div class="lang-modal-radio ${isSelected ? 'checked' : ''}">
                  ${isSelected ? '<div class="lang-modal-radio-inner"></div>' : ''}
                </div>
              </div>
            `;
          }).join('')}
        </div>
        <div class="lang-modal-footer">
          <button class="lang-modal-close-btn" type="button" onclick="window.closeLangModal?.()">
            ${t('close') || 'CLOSE'}
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Create header component
 */
export function Header(
  title: string,
  showBack: boolean = false,
  showLangSwitcher: boolean = true
): string {
  if (typeof document !== 'undefined' && title && title.trim()) {
    document.title = title.trim();
  }
  const backButton = showBack
    ? `<button class="header-back" onclick="window.handleBack?.()">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>`
    : '';
  const canShowSwitcher = showLangSwitcher && shouldShowLanguageSwitcher();
  return `
    <header class="header">
      ${backButton}
      <h1 class="header-title">${title}</h1>
      ${canShowSwitcher ? LanguageSwitcher() : '<div class="header-spacer"></div>'}
    </header>
  `;
}

/**
 * Service navigation tabs component (Dynamic Multi-Industry Config Driven)
 * Reads services from config.services, falls back seamlessly to legacy ActiveServicesState for hotels.
 * Dynamically hides disabled services, and hides the navigation bar entirely if <= 1 service is active.
 */
export function renderServiceTabs(activePage: string, activeServiceId?: string): string {
  const lang = getCurrentLanguage();
  
  // Try dynamic config services first
  try {
    const configServices = getConfigServices();
    if (Array.isArray(configServices) && configServices.length > 0) {
      if (configServices.length <= 1) {
        return '';
      }

      return `
        <div class="nav-service-tabs-container" style="display:flex;background:#fff;border-bottom:1px solid #e0e0e0;position:sticky;top:56px;z-index:90;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;scroll-behavior:smooth;">
          ${configServices.map((svc: any) => {
            const rawLabel = svc.label;
            const labelText = typeof rawLabel === 'object'
              ? (rawLabel[lang] || rawLabel.en || rawLabel.th || svc.id)
              : (rawLabel || svc.id);

            const isCurrent = activeServiceId === svc.id ||
              (svc.id === 'rooms' && activePage === 'search') ||
              (svc.id === 'spa' && activePage === 'spa') ||
              (svc.id === 'restaurant' && activePage === 'restaurant') ||
              (svc.id === 'mice' && activePage === 'mice') ||
              (svc.id === 'room_service' && (activePage === 'room-service' || activePage === 'room_service'));

            let clickHandler = `window.switchService?.('${svc.id}')`;
            if (svc.engine === 'reservation') {
              clickHandler = `window.navigateTo?.('search')`;
            } else if (svc.id === 'spa' && svc.outletType === 'spa') {
              clickHandler = `window.navigateTo?.('spa')`;
            } else if (svc.id === 'restaurant' && svc.outletType === 'restaurant') {
              clickHandler = `window.resetRestOutlet?.();window.navigateTo?.('restaurant')`;
            } else if (svc.id === 'mice' && svc.outletType === 'mice') {
              clickHandler = `window.navigateTo?.('mice')`;
            } else if (svc.id === 'room_service' || svc.engine === 'service_ticket') {
              clickHandler = `window.navigateTo?.('room-service')`;
            }

            return `
              <button class="nav-service-tab ${isCurrent ? 'active' : ''}" onclick="${clickHandler}" style="flex:1 0 auto;min-width:max-content;padding:12px 14px;text-align:center;border:none;background:none;font-weight:${isCurrent ? '600' : '500'};color:${isCurrent ? '#00B900' : '#666'};border-bottom:${isCurrent ? '2px solid #00B900' : '2px solid transparent'};cursor:pointer;white-space:nowrap;font-size:13px;box-sizing:border-box;">
                ${labelText}
              </button>
            `;
          }).join('')}
        </div>
      `;
    }
  } catch (_) {}

  // Fallback to legacy hotel active services
  const services = getActiveServices();
  const activeCount = (services.rooms ? 1 : 0) + (services.spa ? 1 : 0) + (services.restaurant ? 1 : 0) + (services.mice ? 1 : 0);

  // If 0 or 1 service is active, hide navigation bar completely!
  if (activeCount <= 1) {
    return '';
  }

  const tabDict: Record<string, { rooms: string; spa: string; restaurant: string; mice: string; roomService: string }> = {
    th: { rooms: '🏨 ห้องพัก', spa: '🌿 สปา', restaurant: '🍽️ ร้านอาหาร', mice: '🏛️ ประชุม-จัดเลี้ยง', roomService: '🛎️ บริการในห้อง' },
    en: { rooms: '🏨 Rooms', spa: '🌿 Spa', restaurant: '🍽️ Dining', mice: '🏛️ MICE', roomService: '🛎️ Room Care' },
    zh: { rooms: '🏨 客房', spa: '🌿 水疗', restaurant: '🍽️ 餐厅', mice: '🏛️ 会议宴会', roomService: '🛎️ 客房服务' },
    ru: { rooms: '🏨 Номера', spa: '🌿 Спа', restaurant: '🍽️ Ресторан', mice: '🏛️ MICE', roomService: '🛎️ Обслуживание' },
    ja: { rooms: '🏨 客室', spa: '🌿 スパ', restaurant: '🍽️ ダイニング', mice: '🏛️ MICE', roomService: '🛎️ ルームサービス' },
    ko: { rooms: '🏨 객실', spa: '🌿 스파', restaurant: '🍽️ 다이닝', mice: '🏛️ MICE', roomService: '🛎️ 룸 서비스' },
  };
  const tabLabels = tabDict[lang] || tabDict.en;

  return `
    <div class="nav-service-tabs-container" style="display:flex;background:#fff;border-bottom:1px solid #e0e0e0;position:sticky;top:56px;z-index:90;overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none;scroll-behavior:smooth;">
      ${services.rooms ? `
        <button class="nav-service-tab ${activePage === 'search' ? 'active' : ''}" onclick="window.navigateTo?.('search')" style="flex:1 0 auto;min-width:max-content;padding:12px 14px;text-align:center;border:none;background:none;font-weight:${activePage === 'search' ? '600' : '500'};color:${activePage === 'search' ? '#00B900' : '#666'};border-bottom:${activePage === 'search' ? '2px solid #00B900' : '2px solid transparent'};cursor:pointer;white-space:nowrap;font-size:13px;box-sizing:border-box;">
          ${tabLabels.rooms}
        </button>
      ` : ''}
      ${services.spa ? `
        <button class="nav-service-tab ${activePage === 'spa' ? 'active' : ''}" onclick="window.navigateTo?.('spa')" style="flex:1 0 auto;min-width:max-content;padding:12px 14px;text-align:center;border:none;background:none;font-weight:${activePage === 'spa' ? '600' : '500'};color:${activePage === 'spa' ? '#00B900' : '#666'};border-bottom:${activePage === 'spa' ? '2px solid #00B900' : '2px solid transparent'};cursor:pointer;white-space:nowrap;font-size:13px;box-sizing:border-box;">
          ${tabLabels.spa}
        </button>
      ` : ''}
      ${services.restaurant ? `
        <button class="nav-service-tab ${activePage === 'restaurant' ? 'active' : ''}" onclick="window.resetRestOutlet?.();window.navigateTo?.('restaurant')" style="flex:1 0 auto;min-width:max-content;padding:12px 14px;text-align:center;border:none;background:none;font-weight:${activePage === 'restaurant' ? '600' : '500'};color:${activePage === 'restaurant' ? '#00B900' : '#666'};border-bottom:${activePage === 'restaurant' ? '2px solid #00B900' : '2px solid transparent'};cursor:pointer;white-space:nowrap;font-size:13px;box-sizing:border-box;">
          ${tabLabels.restaurant}
        </button>
      ` : ''}
      ${services.mice ? `
        <button class="nav-service-tab ${activePage === 'mice' ? 'active' : ''}" onclick="window.navigateTo?.('mice')" style="flex:1 0 auto;min-width:max-content;padding:12px 14px;text-align:center;border:none;background:none;font-weight:${activePage === 'mice' ? '600' : '500'};color:${activePage === 'mice' ? '#00B900' : '#666'};border-bottom:${activePage === 'mice' ? '2px solid #00B900' : '2px solid transparent'};cursor:pointer;white-space:nowrap;font-size:13px;box-sizing:border-box;">
          ${tabLabels.mice}
        </button>
      ` : ''}
    </div>
  `;
}

/**
 * Create loading spinner
 */
export function LoadingSpinner(message?: string): string {
  return `
    <div class="loading-container">
      <div class="spinner"></div>
      <p>${message || t('loading')}</p>
    </div>
  `;
}

/**
 * Create error message
 */
export function ErrorMessage(message: string, onRetry?: () => void): string {
  const retryButton = onRetry
    ? `<button class="btn btn-primary" onclick="window.handleRetry?.()">${t('retry')}</button>`
    : '';

  return `
    <div class="error-container">
      <div class="error-icon">⚠️</div>
      <p class="error-message">${message}</p>
      ${retryButton}
    </div>
  `;
}

/**
 * Hotel Card - displays hotel info at top of search
 * Pass hotelInfo from API or leave empty for loading state
 */
export function HotelCard(hotelInfo?: {
  name: string;
  address: string;
  rating: number;
  image: string;
}): string {
  // If no hotel info, show loading placeholder
  if (!hotelInfo) {
    return `
      <div class="hotel-card hotel-card-loading">
        <div class="hotel-card-content">
          <div class="hotel-card-image skeleton"></div>
          <div class="hotel-card-info">
            <div class="skeleton skeleton-text" style="width: 80%; height: 20px;"></div>
            <div class="skeleton skeleton-text" style="width: 40%; height: 16px; margin-top: 8px;"></div>
            <div class="skeleton skeleton-text" style="width: 60%; height: 14px; margin-top: 8px;"></div>
          </div>
        </div>
      </div>
    `;
  }

  const stars = '⭐'.repeat(hotelInfo.rating);

  return `
    <div class="hotel-card">
      <div class="hotel-card-content">
        <div class="hotel-card-image">
          <img src="${hotelInfo.image}" alt="${hotelInfo.name}" loading="lazy" />
        </div>
        <div class="hotel-card-info">
          <h2 class="hotel-card-name">${hotelInfo.name}</h2>
          <div class="hotel-card-rating">${stars}</div>
          <p class="hotel-card-address">📍 ${hotelInfo.address}</p>
        </div>
      </div>
    </div>
  `;
}

/**
 * Step indicator for booking flow
 */
export function StepIndicator(currentStep: number): string {
  const steps = [
    { num: 1, label: t('stepSearch'), icon: '🔍' },
    { num: 2, label: t('stepRoom'), icon: '🛏️' },
    { num: 3, label: t('stepGuest'), icon: '👤' },
    { num: 4, label: t('stepConfirm'), icon: '✓' },
  ];

  return `
    <div class="step-indicator">
      ${steps
        .map((step, index) => {
          const isCompleted = currentStep > step.num;
          const isActive = currentStep === step.num;
          const lineCompleted = currentStep > step.num; // Line is green if this step is completed

          return `
          <div class="step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}">
            <div class="step-circle">
              ${
                isCompleted
                  ? `
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
              `
                  : step.num
              }
            </div>
            <span class="step-label">${step.label}</span>
          </div>
          ${index < steps.length - 1 ? `<div class="step-line ${lineCompleted ? 'completed' : ''}"></div>` : ''}
        `;
        })
        .join('')}
    </div>
  `;
}

/**
 * Floating footer with Back / Forward buttons
 */
export function FloatingNav(disableNext: boolean = false): string {
  return `
    <nav class="floating-footer" role="navigation" aria-label="Footer Navigation">
      <div class="floating-footer-inner">
        <button 
          class="floating-circle floating-back" 
          onclick="(function(){ try{ if(window.handleBack) { window.handleBack(); return; } history.back(); }catch(e){ console.error(e); } })()" 
          title="${t('back')}">
          ◀
        </button>
        <button 
          class="floating-circle floating-forward" 
          ${disableNext ? 'disabled' : ''}
          onclick="(function(){ try{ if(window.handleForward) { window.handleForward(); return; } var btn = document.querySelector('.btn.btn-primary, .btn-primary, button[type=submit]'); if (btn) { btn.click(); return; } }catch(e){ console.error(e); } })()" 
          title="${t('next')}">
          ▶
        </button>
      </div>
    </nav>
  `;
}

/**
 * Floating "BACK TO TOP" button component
 */
export function BackToTopButton(pageId: string): string {
  return `
    <button
      id="${pageId}-back-to-top"
      type="button"
      aria-label="${t('backToTop')}"
      style="
        display: none;
        position: fixed;
        bottom: 24px;
        right: 16px;
        z-index: 2000;
        padding: 8px 14px;
        background: #222222;
        color: #ffffff;
        border: none;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.8px;
        text-transform: uppercase;
        cursor: pointer;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
        white-space: nowrap;
        user-select: none;
        -webkit-user-select: none;
      "
    >
      ${t('backToTop')}
    </button>
  `;
}

/**
 * Initialize scroll listener for "BACK TO TOP" button
 */
export function initBackToTop(pageId: string): void {
  const btn = document.getElementById(`${pageId}-back-to-top`) as HTMLButtonElement | null;
  if (!btn) return;

  // Detect Safari only (excluding Chrome, WeChat, LINE, etc.)
  const isSafari = typeof navigator !== 'undefined'
    && /Safari/i.test(navigator.userAgent)
    && !/Chrome|CriOS|FxiOS|EdgiOS|MicroMessenger|Line\//i.test(navigator.userAgent);

  if (isSafari && pageId === 'rest') {
    btn.style.transition = 'bottom 0.25s ease';
  }

  const onScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    btn.style.display = scrollTop > 150 ? 'block' : 'none';

    if (isSafari && pageId === 'rest') {
      const cartEl = document.getElementById('cart-bar');
      const hasCart = cartEl && cartEl.style.display !== 'none';
      btn.style.bottom = hasCart
        ? 'calc(136px + max(env(safe-area-inset-bottom, 0px), 8px))'
        : 'calc(76px + max(env(safe-area-inset-bottom, 0px), 8px))';
    }
  };

  btn.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  onScroll();
}

/**
 * Create button
 */
export function Button(
  text: string,
  onClick: string,
  variant: 'primary' | 'secondary' | 'outline' = 'primary',
  disabled: boolean = false
): string {
  return `
    <button 
      class="btn btn-${variant}" 
      onclick="${onClick}"
      ${disabled ? 'disabled' : ''}
    >
      ${text}
    </button>
  `;
}

/**
 * Create input field
 */
export function InputField(
  label: string,
  name: string,
  type: string = 'text',
  value: string = '',
  required: boolean = false,
  placeholder: string = '',
  autocomplete?: string
): string {
  const autoMap: Record<string, string> = {
    firstName: 'given-name',
    lastName: 'family-name',
    email: 'email',
    phone: 'tel',
    nationality: 'country-name',
  };
  const autoVal = autocomplete || autoMap[name] || 'on';

  return `
    <div class="form-group">
      <label class="form-label" for="${name}">
        ${label}
        ${required ? '<span class="required">*</span>' : ''}
      </label>
      <input 
        type="${type}"
        id="${name}"
        name="${name}"
        class="form-input"
        value="${value}"
        placeholder="${placeholder}"
        autocomplete="${autoVal}"
        ${required ? 'required' : ''}
      />
      <span class="field-error" id="${name}-error"></span>
    </div>
  `;
}

/**
 * Create select field
 */
export function SelectField(
  label: string,
  name: string,
  options: { value: string; text: string }[],
  selected: string = '',
  required: boolean = false
): string {
  const optionsHtml = options
    .map(
      opt =>
        `<option value="${opt.value}" ${opt.value === selected ? 'selected' : ''}>${opt.text}</option>`
    )
    .join('');

  return `
    <div class="form-group">
      <label class="form-label" for="${name}">
        ${label}
        ${required ? '<span class="required">*</span>' : ''}
      </label>
      <select id="${name}" name="${name}" class="form-select" ${required ? 'required' : ''}>
        ${optionsHtml}
      </select>
    </div>
  `;
}

/**
 * Create textarea
 */
export function TextArea(
  label: string,
  name: string,
  value: string = '',
  placeholder: string = '',
  rows: number = 3
): string {
  return `
    <div class="form-group">
      <label class="form-label" for="${name}">${label}</label>
      <textarea 
        id="${name}"
        name="${name}"
        class="form-textarea"
        placeholder="${placeholder}"
        rows="${rows}"
      >${value}</textarea>
    </div>
  `;
}

/**
 * Create card component
 */
export function Card(content: string, className: string = ''): string {
  return `
    <div class="card ${className}">
      ${content}
    </div>
  `;
}

/**
 * Create room card
 */
export function RoomCard(
  room: {
    roomTypeCode: string;
    roomTypeName: string;
    ratePlanCode: string;
    ratePlanName: string;
    totalRate: number;
    ratePerRoom: number;
    averageNightlyRate: number;
    numberOfRooms: number;
    currency: string;
    available: number;
    description?: string;
    bedType?: string;
    bedTypeDesc?: string;
    amenities?: string[];
    cancellationPolicy?: string;
    mealPlan?: string;
    isRefundable?: boolean;
  },
  nights: number,
  onSelect: string
): string {
  // Get translated names
  const roomTypeName = translateRoomType(room.roomTypeCode, room.roomTypeName);
  const ratePlanName = translateRatePlan(room.ratePlanCode, room.ratePlanName);
  const mealPlanDesc = translateRatePlanDescription(room.ratePlanCode, room.mealPlan);

  // Translate room description
  const translatedDescription = translateRoomDescription(room.roomTypeCode, room.description);

  // Room description
  const descriptionHtml = translatedDescription
    ? `<p class="room-description">${translatedDescription}</p>`
    : '';

  // Amenities with icons
  const amenityIcons: Record<string, string> = {
    WIFI: '📶',
    TV: '📺',
    MINIBAR: '🍷',
    SAFE: '🔒',
    AC: '❄️',
    HAIRDRYER: '💨',
    BATHTUB: '🛁',
    SOFA: '🛋️',
    JACUZZI: '♨️',
    LIVING_ROOM: '🏠',
    BALCONY: '🏖️',
    DESK: '💼',
    LOUNGE_ACCESS: '👔',
  };

  const amenitiesHtml = room.amenities?.length
    ? `<div class="room-amenities">
        ${room.amenities
          .slice(0, 6)
          .map(code => {
            const icon = getCustomAmenityIcon(code) || amenityIcons[code] || '✓';
            const label = translateAmenity(code);
            return `<span class="amenity-item" title="${label}">${icon} ${label}</span>`;
          })
          .join('')}
       </div>`
    : '';

  return `
    <div class="room-card">
      <div class="room-header">
        <h3 class="room-name">${roomTypeName}</h3>
        <span class="room-available">${Number.isFinite(room.available) ? room.available : 1} ${t('rooms')}</span>
      </div>
      ${room.bedType ? `<div class="room-bed-type" style="font-size:12px; color:#555; font-weight:600; margin-top:2px; margin-bottom:6px;">🛏️ ${room.bedType}${room.bedTypeDesc ? ` (${room.bedTypeDesc})` : ''}</div>` : ''}
      ${descriptionHtml}
      ${amenitiesHtml}
      ${room.cancellationPolicy ? `<p class="room-policy"><span class="icon">📋</span> ${room.cancellationPolicy}</p>` : ''}

      <!-- Room images carousel placeholder -->
      <div class="room-images" data-room-type="${room.roomTypeCode}"></div>
      <div class="room-pricing">
        <div class="price-nightly">
          <span class="price-amount">${formatPrice(room.averageNightlyRate, room.currency)}<sup>++</sup></span>
          <span class="price-unit">${t('perNight')}</span>
        </div>
        <div class="price-note">${t('excludingTaxes')}</div>
        <div class="price-total">
          ${
            room.numberOfRooms > 1
              ? `${room.numberOfRooms} ${t('rooms')} × ${nights} ${t('nights')}: <strong>${formatPrice(room.totalRate, room.currency)}</strong> <span class="tax-note">${t('inclTaxService')}</span>`
              : `${t('totalFor')} ${nights} ${t('nights')}: <strong>${formatPrice(room.totalRate, room.currency)}</strong> <span class="tax-note">${t('inclTaxService')}</span>`
          }
        </div>
      </div>
      <div class="room-rate-details">
        <span class="rate-plan-name">${ratePlanName}</span>${mealPlanDesc ? ` - <span class="rate-plan-desc">${mealPlanDesc}</span>` : ''}${room.isRefundable === false ? ` <span style="display:inline-block; background:#dc2626; color:#fff; font-size:11px; font-weight:600; padding:1px 7px; border-radius:4px; margin-left:4px;">${t('nonRefundable')}</span>` : ''}
      </div>
      <button class="btn btn-primary btn-block" onclick="${onSelect}">
        ${t('selectRoom')}
      </button>
    </div>
  `;
}

/**
 * Format price with currency
 */
export function formatPrice(amount: number, currency: string = 'THB'): string {
  const validAmount = Number.isFinite(amount) ? amount : 0;
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: currency || 'THB',
    minimumFractionDigits: 0,
  }).format(validAmount);
}

export interface GroupedRoomPlanItem {
  originalIndex: number;
  room: {
    roomTypeCode: string;
    roomTypeName: string;
    ratePlanCode: string;
    ratePlanName: string;
    totalRate: number;
    ratePerRoom: number;
    averageNightlyRate: number;
    numberOfRooms: number;
    currency: string;
    available: number;
    description?: string;
    bedType?: string;
    bedTypeDesc?: string;
    amenities?: string[];
    cancellationPolicy?: string;
    mealPlan?: string;
    isRefundable?: boolean;
    hotelCode?: string;
  };
}

export interface GroupedRoomType {
  roomTypeCode: string;
  roomTypeName: string;
  description?: string;
  bedType?: string;
  bedTypeDesc?: string;
  amenities?: string[];
  available: number;
  plans: GroupedRoomPlanItem[];
}

/**
 * Create grouped room card with expandable rate plans accordion
 */
export function GroupedRoomCard(
  group: GroupedRoomType,
  nights: number
): string {
  if (!group.plans || group.plans.length === 0) return '';

  const initialPlan = group.plans[0];
  const lowestPriceStr = formatPrice(initialPlan.room.averageNightlyRate, initialPlan.room.currency);

  // Get translated names
  const roomTypeName = translateRoomType(group.roomTypeCode, group.roomTypeName);

  // Translate room description
  const translatedDescription = translateRoomDescription(group.roomTypeCode, group.description);
  const descriptionHtml = translatedDescription
    ? `<p class="room-description">${translatedDescription}</p>`
    : '';

  // Amenities with icons
  const amenityIcons: Record<string, string> = {
    WIFI: '📶',
    TV: '📺',
    MINIBAR: '🍷',
    SAFE: '🔒',
    AC: '❄️',
    HAIRDRYER: '💨',
    BATHTUB: '🛁',
    SOFA: '🛋️',
    JACUZZI: '♨️',
    LIVING_ROOM: '🏠',
    BALCONY: '🏖️',
    DESK: '💼',
    LOUNGE_ACCESS: '👔',
  };

  const amenitiesHtml = group.amenities?.length
    ? `<div class="room-amenities">
        ${group.amenities
          .slice(0, 6)
          .map(code => {
            const icon = getCustomAmenityIcon(code) || amenityIcons[code] || '✓';
            const label = translateAmenity(code);
            return `<span class="amenity-item" title="${label}">${icon} ${label}</span>`;
          })
          .join('')}
       </div>`
    : '';

  // Rate plan items inside accordion
  const ratePlansHtml = group.plans
    .map(p => {
      const planName = translateRatePlan(p.room.ratePlanCode, p.room.ratePlanName);
      const mealPlanDesc = translateRatePlanDescription(p.room.ratePlanCode, p.room.mealPlan);

      return `
        <div class="rate-plan-item">
          <div class="rate-plan-content">
            <div class="rate-plan-header">
              <h4 class="rate-plan-title">${planName}</h4>
              ${
                p.room.isRefundable === false
                  ? `<span class="badge-non-refundable">${t('nonRefundable')}</span>`
                  : `<span class="badge-refundable">✓ ${t('refundablePolicy')}</span>`
              }
            </div>

            ${mealPlanDesc ? `<div class="rate-plan-benefit">🍳 ${mealPlanDesc}</div>` : ''}
            ${p.room.cancellationPolicy ? `<div class="rate-plan-policy">📋 ${p.room.cancellationPolicy}</div>` : ''}

            <div class="rate-plan-pricing">
              <div class="price-nightly">
                <span class="price-amount">${formatPrice(p.room.averageNightlyRate, p.room.currency)}<sup>++</sup></span>
                <span class="price-unit">${t('perNight')}</span>
              </div>
              <div class="price-note">${t('excludingTaxes')}</div>
              <div class="price-total">
                ${
                  p.room.numberOfRooms > 1
                    ? `${p.room.numberOfRooms} ${t('rooms')} × ${nights} ${t('nights')}: <strong>${formatPrice(p.room.totalRate, p.room.currency)}</strong> <span class="tax-note">${t('inclTaxService')}</span>`
                    : `${t('totalFor')} ${nights} ${t('nights')}: <strong>${formatPrice(p.room.totalRate, p.room.currency)}</strong> <span class="tax-note">${t('inclTaxService')}</span>`
                }
              </div>
            </div>
          </div>

          <div class="rate-plan-action">
            <button type="button" class="btn btn-primary btn-book-rate" onclick="window.selectRoom(${p.originalIndex})">
              ${t('selectRoom')}
            </button>
          </div>
        </div>
      `;
    })
    .join('');

  return `
    <div class="room-card grouped-room-card" data-room-type="${group.roomTypeCode}">
      <!-- Room images carousel placeholder (1 per room type) -->
      <div class="room-images" data-room-type="${group.roomTypeCode}"></div>

      <div class="room-card-body">
        <div class="room-card-header">
          <div class="room-meta">
            <h3 class="room-name">${roomTypeName}</h3>
            ${group.bedType ? `<div class="room-bed-type">🛏️ ${group.bedType}${group.bedTypeDesc ? ` (${group.bedTypeDesc})` : ''}</div>` : ''}
            <span class="room-available">${Number.isFinite(group.available) ? group.available : 1} ${t('rooms')}</span>
          </div>

          <div class="room-toggle-area">
            <button
              type="button"
              class="btn-toggle-rates"
              data-room-type="${group.roomTypeCode}"
              data-lowest-text="${t('startingFrom')} ${lowestPriceStr}"
              data-hide-text="${t('hideRates')}"
            >
              <span class="toggle-text">${t('startingFrom')} ${lowestPriceStr}</span>
              <span class="toggle-icon">▾</span>
            </button>
          </div>
        </div>

        ${descriptionHtml}
        ${amenitiesHtml}
      </div>

      <!-- Collapsible rate plans container -->
      <div class="rate-plans-container" id="rate-plans-${group.roomTypeCode}" style="display: none;">
        <div class="rate-plans-list">
          ${ratePlansHtml}
        </div>
      </div>
    </div>
  `;
}

/**
 * Create summary row
 */
export function SummaryRow(label: string, value: string, highlight: boolean = false): string {
  return `
    <div class="summary-row ${highlight ? 'highlight' : ''}">
      <span class="summary-label">${label}</span>
      <span class="summary-value">${value}</span>
    </div>
  `;
}

/**
 * Extra charge confirmation modal
 */
export function ExtraChargeModal(
  extraAdults: number,
  extraChildren: number,
  extraAdultCharge: number,
  extraChildCharge: number,
  totalExtraCharge: number,
  currency: string,
  _onConfirm: () => void,
  _onCancel: () => void
): string {
  const formatMoney = (amount: number) => formatPrice(amount, currency);

  return `
    <div class="modal-overlay" id="extra-charge-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>⚠️ ${t('capacityExceeded') || 'จำนวนผู้เข้าพักเกินกำหนด'}</h3>
        </div>
        <div class="modal-body">
          <p style="margin-bottom: 16px;">${t('capacityExceededMessage') || 'จำนวนผู้เข้าพักของคุณเกินขีดจำกัดของห้องพัก คุณต้องการชำระค่าบริการเพิ่มหรือไม่?'}</p>
          
          <div class="extra-charges-detail">
            ${
              extraAdults > 0
                ? `
              <div class="charge-row">
                <span>${t('extraAdults') || 'ผู้ใหญ่เพิ่ม'}: ${extraAdults} ${t('person') || 'คน'}</span>
                <span>${formatMoney(extraAdultCharge)} / ${t('personPerNight') || 'คน/คืน'}</span>
              </div>
            `
                : ''
            }
            ${
              extraChildren > 0
                ? `
              <div class="charge-row">
                <span>${t('extraChildren') || 'เด็กเพิ่ม'}: ${extraChildren} ${t('person') || 'คน'}</span>
                <span>${formatMoney(extraChildCharge)} / ${t('personPerNight') || 'คน/คืน'}</span>
              </div>
            `
                : ''
            }
            <div class="charge-row total">
              <strong>${t('totalExtraCharge') || 'ค่าบริการเพิ่มทั้งหมด'}</strong>
              <strong>${formatMoney(totalExtraCharge)}</strong>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.handleExtraChargeCancel?.()">${t('cancel') || 'ยกเลิก'}</button>
          <button class="btn btn-primary" onclick="window.handleExtraChargeConfirm?.()">${t('confirm') || 'ยืนยัน'}</button>
        </div>
      </div>
    </div>
  `;
}

/**
 * App Version Footer - Subtle bottom text showing version and branding
 * Free Tier: "Powered by Revio • v3.8.93 (live)" where Revio links to https://www.metagoldtech.com
 * Paid Tier: "v3.8.93 (live)" clean without vendor branding
 */
export function AppVersionFooter(): string {
  const semver = (import.meta as any).env?.VITE_APP_SEMVER || 'v3.8.93';
  const hash = (import.meta as any).env?.VITE_BUILD_VERSION || 'live';
  const tenantConfig = getCachedTenantConfig();
  const plan = tenantConfig?.plan || 'free';
  const isFreeTier = plan === 'free' || tenantConfig?.brandingBadge !== false;
  const brandingUrl = tenantConfig?.brandingLink || 'https://www.metagoldtech.com';

  const brandingHtml = isFreeTier
    ? `<span>Powered by </span><a href="${brandingUrl}" target="_blank" rel="noopener noreferrer" style="color:inherit;text-decoration:underline;text-underline-offset:2px;font-weight:600;opacity:0.9;">Revio</a> • `
    : '';

  return `
    <div class="app-version-footer" style="text-align:center;padding:20px 0 28px;font-size:11px;color:#bbb;font-family:monospace;letter-spacing:0.3px;user-select:none;">
      ${brandingHtml}v${semver.replace(/^v/, '')} (${hash})
    </div>
  `;
}
